import React, { useRef, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Upload, ImagePlus, X, Loader2 } from 'lucide-react';

/**
 * Drag/drop + click-to-pick image uploader.
 * Props:
 *   - value: current image URL (string)
 *   - onChange: (newUrl|null) => void
 *   - label?: header label
 *   - aspect?: 'square' | 'video' | 'wide'
 */
export const ImageUploader = ({ value, onChange, label = 'Image', aspect = 'square' }) => {
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL || '';

  const resolved = value && value.startsWith('/api/') ? `${backend}${value}` : value;

  const aspectClass = aspect === 'video' ? 'aspect-video' : aspect === 'wide' ? 'aspect-[16/9]' : 'aspect-square';

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please pick an image file'); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error('Image is larger than 8 MB'); return; }
    setUploading(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/uploads', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      onChange(data.url);
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false); setProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
    e.target.value = '';
  };

  return (
    <div data-testid="image-uploader">
      <div className="text-[11px] uppercase tracking-widest text-white/55 mb-1">{label}</div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        style={{ maxHeight: 220 }}
        className={`relative ${aspectClass} max-w-md rounded-md border-2 border-dashed cursor-pointer transition-colors overflow-hidden ${drag ? 'border-[hsl(var(--sb-gold))] bg-[hsl(var(--sb-gold))]/10' : 'border-[hsl(var(--sb-border))] hover:border-white/35 bg-[hsl(var(--sb-surface))]'}`}
      >
        {resolved ? (
          <>
            <img src={resolved} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 hover:opacity-100 transition-opacity grid place-items-center">
              <span className="text-sm text-white inline-flex items-center gap-2"><Upload size={14} />Replace</span>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <ImagePlus size={28} className="text-[hsl(var(--sb-gold))]" />
            <div className="mt-2 text-sm text-white">Drag & drop or click to upload</div>
            <div className="text-[11px] text-white/55 mt-0.5">PNG, JPG, WEBP or GIF · up to 8 MB</div>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/70 grid place-items-center">
            <div className="text-center">
              <Loader2 size={26} className="animate-spin text-[hsl(var(--sb-gold))] mx-auto" />
              <div className="text-sm text-white mt-2">Uploading… {progress}%</div>
              <div className="w-40 mx-auto h-1.5 bg-white/15 rounded-full mt-2 overflow-hidden">
                <div className="h-full bg-[hsl(var(--sb-gold))] transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="text-[11px] text-white/45 truncate max-w-[60%]" title={value}>{value || 'No image selected'}</div>
        {value && (
          <button onClick={() => onChange(null)} className="inline-flex items-center gap-1 text-xs text-white/65 hover:text-[hsl(var(--sb-red))]"><X size={12} />Remove</button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={onPick} />
    </div>
  );
};
